import { useEffect, useState } from 'react';
import { useAmplifyData } from '../hooks/useAmplifyData';
import type { DataItem as DataItemType, ActivityLog as ActivityLogType } from '../models/index.js';
import { DataItem as DataItemModel, ActivityLog as ActivityLogModel } from '../models/index.js';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import './Screen.css';

// Helper to get sync status (works in both Amplify and local mode)
const getSyncStatus = async () => {
  try {
    const { AmplifyDataService } = await import('../services/amplifyDataService');
    return await AmplifyDataService.getSyncStatus();
  } catch {
    return { isOnline: navigator.onLine, syncStatus: 'Local mode' };
  }
};

interface DashboardStats {
  totalItems: number;
  syncStatus: string;
}

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    totalItems: 0,
    syncStatus: 'Unknown',
  });
  const isOnline = useNetworkStatus();
  const { data: dataItems } = useAmplifyData<DataItemType>(DataItemModel);
  const { data: recentActivity, loading } = useAmplifyData<ActivityLogType>(ActivityLogModel, {
    sort: (a, b) => {
      const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return timeB - timeA;
    },
    limit: 20,
  });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const syncStatus = await getSyncStatus();
        setStats({
          totalItems: dataItems.length,
          syncStatus: syncStatus.isOnline ? 'Online - Auto-syncing' : 'Offline - Will sync when online',
        });
      } catch (err) {
        // Local mode fallback
        setStats({
          totalItems: dataItems.length,
          syncStatus: isOnline ? 'Local Mode - No cloud sync' : 'Offline - Local Mode',
        });
      }
    };

    loadStats();
    const interval = setInterval(loadStats, 5000);
    return () => clearInterval(interval);
  }, [dataItems.length, isOnline]);

  return (
    <div className="screen">
      <div className="screen-header">
        <h1>Dashboard</h1>
        <p className="screen-subtitle">Overview of your application</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>Total Items</h3>
            <p className="stat-value">{stats.totalItems}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">🔄</div>
          <div className="stat-content">
            <h3>Sync Status</h3>
            <p className="stat-value" style={{ fontSize: '14px' }}>{stats.syncStatus}</p>
          </div>
        </div>
      </div>

      <div className="content-section">
        <h2>Recent Activity</h2>
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <div className="activity-list">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity: any, index: number) => (
                <div key={index} className="activity-item">
                  <span className="activity-time">
                    {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : ''}
                  </span>
                  <span className="activity-description">{activity.description}</span>
                </div>
              ))
            ) : (
              <p className="empty-state">No recent activity</p>
            )}
          </div>
        )}
      </div>

      <div className="content-section">
        <h2>Quick Actions</h2>
        <div className="action-buttons">
          <button className="btn btn-secondary" onClick={() => window.location.reload()}>
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
}

